import {
  ListManager, PageHeader, Paginator, Styles,
} from '@/components'
import useAuth from '@/hooks/useAuth'
import useAxiosRequest from '@/hooks/useAxiosRequest'
import usePageTitle from '@/hooks/usePageTitle'
import useQuery from '@/hooks/useQuery'
import adoptionFairs from '@/services/api/adoptionFairs'
import chats from '@/services/api/chats'
import MessagesModal from '@/views/app/Messages/Modal'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import AdoptionFairMessagesFilter from './components/Filter'
import Row from './components/Row'

function onlyUnreadFilterIsActive(query) {
  return query?.q?.onlyUnread === true || query?.q?.onlyUnread === 'true'
}

const messageService = {
  list: adoptionFairs.conversations,
  history: (conversation) => chats.messages(conversation.conversationId),
  addMessage: (conversation, body) => chats.addMessage(conversation.conversationId, body),
  read: (conversation) => chats.readChat(conversation.conversationId),
}

function AdoptionFairMessages({ title }) {
  usePageTitle({ title })
  const query = useQuery()
  const history = useHistory()
  const location = useLocation()
  const { conversationId } = useParams()
  const { licensing } = useAuth()
  const {
    data, error, loading, setData,
  } = useAxiosRequest(messageService.list, query)
  const onlyUnread = onlyUnreadFilterIsActive(query)

  const selected = useMemo(() => {
    const selectedFromList = data?.rows?.find((conversation) => (
      Number(conversation.conversationId) === Number(conversationId)
    ))
    const selectedFromLocation = location.state?.conversation

    if (selectedFromList) return selectedFromList
    if (Number(selectedFromLocation?.conversationId) === Number(conversationId)) {
      return selectedFromLocation
    }

    return null
  }, [conversationId, data, location.state])

  const updateConversation = useCallback((conversationIdToUpdate, mapper) => {
    setData((current) => (
      current
        ? {
          ...current,
          rows: current.rows
            ?.map((item) => (
              Number(item.conversationId) === Number(conversationIdToUpdate)
                ? mapper(item)
                : item
            ))
            .filter((item) => (
              !onlyUnread
              || Number(item.conversationId) !== Number(conversationIdToUpdate)
              || item.unreads > 0
            )),
        }
        : current
    ))
  }, [onlyUnread, setData])

  const readConversation = useCallback(async (conversation) => {
    if (!conversation?.unreads) return

    updateConversation(conversation.conversationId, (item) => ({ ...item, unreads: 0 }))

    try {
      await messageService.read(conversation)
    } catch (e) {
      toast.error(e?.message)
    }
  }, [updateConversation])

  const updateConversationLastMessage = useCallback((conversation, message) => {
    if (!message?.id) return
    const lastMessage = {
      ...message,
      attachmentURL: message.attachmentURL || message.images?.[0],
    }

    updateConversation(conversation.conversationId, (item) => ({
      ...item,
      unreads: 0,
      lastMessage,
    }))
  }, [updateConversation])

  function closeModal() {
    history.push(`/app/adoption-fairs/messages${location.search}`)
  }

  return (
    <Styles.Container className="grid-xl">
      <PageHeader
        title="Mensagens"
        subtitle="Visualize e responda às mensagens recebidas nas feiras"
        icon="fas fa-comments"
        rightAction={() => <AdoptionFairMessagesFilter />}
      />
      <ListManager
        loading={loading}
        count={data?.count || 0}
        error={error}
        emptyMessage="Nenhuma conversa"
        emptySubtitle="Ainda não há mensagens recebidas"
        emptyIcon="fas fa-comments"
      >
        {
          Boolean(data) && (
            <div>
              <small>
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Pet</th>
                      <th>Última mensagem</th>
                      <th>Participantes</th>
                      <th>Feira</th>
                      <th width={1} className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((conversation) => (
                      <Row
                        key={`${conversation.type}-${conversation.conversationId}`}
                        conversation={conversation}
                        search={location.search}
                      />
                    ))}
                  </tbody>
                </table>
              </small>
              <Paginator paginatedList={data} />
            </div>
          )
        }
      </ListManager>
      {
        conversationId && (
          <MessagesModal
            selected={selected}
            listLoading={loading && !selected}
            messageService={messageService}
            onClose={closeModal}
            onRead={readConversation}
            onMessageAdded={(message) => updateConversationLastMessage(selected, message)}
            licensing={licensing}
          />
        )
      }
    </Styles.Container>
  )
}

AdoptionFairMessages.propTypes = {
  title: PropTypes.string.isRequired,
}

export default AdoptionFairMessages
